import { Message, MessageContent } from '@crowdex/ui/ai/message';
import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import {
  AudioLines,
  Box,
  CalendarDays,
  ChartColumn,
  Check,
  Clock,
  GitBranch,
  Image,
  type LucideIcon,
  Sheet,
  UserRound,
  Video,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

/**
 * The charter shared by phone and desktop: the Crow charter (brand/crow-charte.html) as Crowdex
 * applies it, drawn with the live tokens: every swatch shows the value the current theme
 * resolves, so the board is also a check of `theme.css`.
 */

type Swatch = Readonly<{ name: string; role?: string; token: string; fill: string }>;

const NEUTRALS: readonly Swatch[] = [
  {
    name: 'Barre / tiroir',
    role: 'navigation',
    token: '--desktop-sidebar',
    fill: 'bg-desktop-sidebar',
  },
  { name: 'Fond', role: 'contenu', token: '--background', fill: 'bg-background' },
  { name: 'Surface', role: 'cartes, saisie', token: '--card', fill: 'bg-card' },
  {
    name: 'Contrôle',
    role: 'pastilles, boutons secondaires',
    token: '--control',
    fill: 'bg-control',
  },
  { name: 'Code', role: 'commandes, terminal', token: '--code-surface', fill: 'bg-code-surface' },
  { name: 'Texte', token: '--foreground', fill: 'bg-foreground' },
  {
    name: 'Texte secondaire',
    role: 'métadonnées',
    token: '--muted-foreground',
    fill: 'bg-muted-foreground',
  },
];

const STATES: readonly Swatch[] = [
  {
    name: 'Action principale',
    role: 'cobalt, un seul bouton plein par zone',
    token: '--primary',
    fill: 'bg-primary',
  },
  { name: 'Ça t’attend', role: 'compagnon ambre', token: '--attention', fill: 'bg-attention' },
  { name: 'En cours', role: 'cobalt, conversation active', token: '--running', fill: 'bg-running' },
  { name: 'Succès', role: 'ajouts, merged', token: '--success', fill: 'bg-success' },
  { name: 'Erreur', role: 'suppressions, échecs', token: '--destructive', fill: 'bg-destructive' },
];

const SHAPES = [
  ['6 px · contrôle', 'rounded-control'],
  ['10 px · panneau', 'rounded-card'],
  ['14 px · fenêtre', 'rounded-window'],
] as const;

// The apps keep their mobile tile colours (Appendix A), from the existing mode palette.
const APPS: readonly Readonly<{ name: string; icon: LucideIcon; tint: string }>[] = [
  { name: 'Design', icon: Image, tint: 'bg-mode-purple/25 text-mode-purple' },
  { name: 'Video', icon: Video, tint: 'bg-mode-red/25 text-mode-red' },
  { name: 'Live', icon: AudioLines, tint: 'bg-mode-green/25 text-mode-green' },
  { name: 'Planner', icon: CalendarDays, tint: 'bg-mode-blue/25 text-mode-blue' },
  { name: 'Automation', icon: Clock, tint: 'bg-mode-amber/25 text-mode-amber' },
  { name: 'Dashboards', icon: ChartColumn, tint: 'bg-mode-cyan/25 text-mode-cyan' },
  { name: 'Sheets', icon: Sheet, tint: 'bg-mode-green/25 text-mode-green' },
  { name: 'Avatars', icon: UserRound, tint: 'bg-mode-red/20 text-mode-red' },
  { name: 'Crowbox', icon: Box, tint: 'bg-control text-muted-foreground' },
];

/** A resolved CSS colour as `#rrggbb`, painted on a 1 px canvas (any colour space reads back). */
function toHex(color: string): string {
  const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (context === null) return color;
  context.fillStyle = color;
  context.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = context.getImageData(0, 0, 1, 1).data;
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The colours the current theme gives the tokens, read again whenever `version` changes. Read a
 * frame later: the page sets the theme in an effect that runs after this one.
 */
function useTokenValues(tokens: readonly string[], version: string): Record<string, string> {
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    void version;
    const frame = requestAnimationFrame(() => {
      const probe = document.createElement('span');
      document.body.append(probe);
      const next: Record<string, string> = {};
      for (const token of tokens) {
        probe.style.color = `var(${token})`;
        next[token] = toHex(getComputedStyle(probe).color);
      }
      probe.remove();
      setValues(next);
    });
    return () => cancelAnimationFrame(frame);
  }, [tokens, version]);
  return values;
}

function CharterCard({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className={cn('min-w-0 rounded-card border bg-card p-5 text-card-foreground', className)}
    >
      <h2 className="type-section mb-3 text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function SwatchList({
  swatches,
  values,
}: {
  swatches: readonly Swatch[];
  values: Record<string, string>;
}) {
  return (
    <ul className="space-y-3.5">
      {swatches.map((swatch) => (
        <li className="flex min-w-0 items-center gap-3" key={swatch.token}>
          <span
            aria-hidden="true"
            className={cn('size-9 shrink-0 rounded-lg border border-hairline', swatch.fill)}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-foreground">{swatch.name}</span>
            {swatch.role && (
              <span className="block truncate text-xs text-muted-foreground">{swatch.role}</span>
            )}
          </span>
          <code className="shrink-0 font-mono text-xs text-subtle-foreground">
            {values[swatch.token] || swatch.token}
          </code>
        </li>
      ))}
    </ul>
  );
}

const TOKENS = [...NEUTRALS, ...STATES].map((swatch) => swatch.token);

/** The DeskCharter board: neutrals, action and states, typography, shapes, apps, components. */
export function CharterBoard({ theme }: { theme: string }) {
  const values = useTokenValues(TOKENS, theme);
  return (
    <div className="space-y-6" data-slot="charter-board">
      <header className="space-y-1.5">
        <h1 className="type-page-title text-foreground">Charte commune mobile · desktop</h1>
        <p className="text-sm text-muted-foreground">
          La charte Crow : les mêmes couleurs, la même police, les mêmes formes sur le téléphone et
          le desktop. Le desktop ajoute de la place, pas un autre style.
        </p>
      </header>
      <div className="grid min-w-0 gap-4 lg:grid-cols-[4fr_4fr_5fr] lg:items-start">
        <CharterCard title="Neutres">
          <SwatchList swatches={NEUTRALS} values={values} />
        </CharterCard>
        <CharterCard title="Action & états">
          <SwatchList swatches={STATES} values={values} />
        </CharterCard>
        <div className="grid min-w-0 gap-4">
          <CharterCard title="Typographie">
            <div className="space-y-3">
              <p className="type-page-title">Titre de page · 26 / 700 · Avenir Next</p>
              <p className="type-card-title">Titre de carte · 16 / 600 · Avenir Next</p>
              <p className="type-reply">Texte de réponse · 15 / 1.5 — Inter.</p>
              <p className="type-meta text-muted-foreground">Métadonnées · 13 · crowdex · 8 min</p>
              <p className="type-section text-muted-foreground">Section · 12 capitales</p>
              <code className="block rounded-code bg-code-surface px-3 py-2 font-mono text-sm text-foreground">
                mono : code, fichiers, commandes seulement
              </code>
            </div>
          </CharterCard>
          <CharterCard title="Formes">
            <ul className="flex flex-wrap gap-x-2 gap-y-3">
              {SHAPES.map(([label, radius]) => (
                <li className="flex flex-col items-center gap-2" key={radius}>
                  <span aria-hidden="true" className={cn('h-11 w-16 bg-control', radius)} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </li>
              ))}
            </ul>
          </CharterCard>
        </div>
      </div>
      <div className="grid min-w-0 gap-4 lg:grid-cols-[4fr_5fr]">
        <CharterCard title="Apps">
          <ul className="flex flex-wrap gap-x-2 gap-y-3">
            {APPS.map(({ name, icon: Icon, tint }) => (
              <li className="flex w-16 flex-col items-center gap-1.5" key={name}>
                <span
                  aria-hidden="true"
                  className={cn('flex size-12 items-center justify-center rounded-tile', tint)}
                >
                  <Icon className="size-5" />
                </span>
                <span className="text-xs text-foreground">{name}</span>
              </li>
            ))}
          </ul>
        </CharterCard>
        <CharterCard title="Composants">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button>Approve</Button>
              <Button variant="outline">Deny</Button>
              <Button variant="secondary">Oui, ouvre-la</Button>
              <Badge mono size="md" className="h-control-compact px-3">
                <GitBranch aria-hidden="true" />
                main
              </Badge>
            </div>
            <Message from="user">
              <MessageContent>Mon message : une bulle à droite.</MessageContent>
            </Message>
            <Badge className="h-control-compact gap-1.5 px-3 font-normal text-muted-foreground">
              <Check aria-hidden="true" className="text-success" />
              Worked 4 min · 12 files read
            </Badge>
            <p className="type-reply rounded-card border border-attention/40 bg-attention/[0.06] px-4 py-2.5">
              Une question en attente porte l’accent.
            </p>
          </div>
        </CharterCard>
      </div>
    </div>
  );
}
